/**
 * Analytics (v2) — the explain/decide layer (s-5pny), a new top-level surface beside
 * Overview. Thin shell only (CD-7): the header, the module tab strip, and the body
 * split-out. The selected module lives in the URL (?module=, useAnalyticsView) so a
 * bridge/pace/driver view is shareable (CD-8); scope (site · cadence · window) comes
 * from the shared scope controls via useScope, preserved across a module switch. The
 * data-bound modules (variance bridges, pace, drivers, benchmarking, contribution)
 * compose inside AnalyticsBody — this file stays composition-free.
 */

import React from 'react'
import clsx from 'clsx'
import ScopeBar from '../components/kit/ScopeBar'
import { entityAxis, grainAxis, periodAxis } from '../components/kit/scopeAxes'
import { useScope } from '../hooks/useScope'
import { useAnalyticsView, type AnalyticsModule } from '../hooks/useAnalyticsView'
import AnalyticsBody from './AnalyticsBody'

function ModuleTabs({
  modules,
  active,
  onSelect,
}: {
  modules: readonly AnalyticsModule[]
  active: string
  onSelect: (id: string) => void
}): React.JSX.Element {
  return (
    <div role="tablist" aria-label="Analytics module" className="flex gap-1 border-b border-line overflow-x-auto cx-scroll">
      {modules.map((m) => (
        <button
          key={m.id}
          role="tab"
          aria-selected={active === m.id}
          onClick={() => onSelect(m.id)}
          className={clsx(
            'cx-btn -mb-px px-4 py-2 text-[13px] font-semibold border-b-2 whitespace-nowrap',
            active === m.id ? 'border-brand text-ink' : 'border-transparent text-mut hover:text-ink'
          )}
        >
          {m.label}
          {m.gated && <span className="ml-1.5 font-mono text-[9px] tracking-[.08em] uppercase text-mut2">gated</span>}
        </button>
      ))}
    </div>
  )
}

export default function Analytics(): React.JSX.Element {
  const scope = useScope()
  const { module, modules, setModule } = useAnalyticsView()
  return (
    <div className="cx-fade cx-page py-6 pb-20 space-y-4">
      <header>
        <div className="font-mono text-[10px] tracking-[.16em] uppercase mb-1.5" style={{ color: 'var(--blue)' }}>
          Analytics · explain / decide
        </div>
        <h1 className="text-[26px] font-bold tracking-[-.02em] text-ink">Analytics</h1>
        <p className="mt-1.5 text-[13.5px] text-mut max-w-[660px]">
          Why did it change, what drives it, where is it concentrated, will we hit target — the analytical layer above
          the scorecard&apos;s monitor and the charts&apos; track. Integrity carries through: a decomposition whose leg
          is known-wrong wears the flag rather than laundering it into a clean waterfall.
        </p>
      </header>
      <ScopeBar axes={[entityAxis(scope), grainAxis(scope), periodAxis()]} />
      <ModuleTabs modules={modules} active={module.id} onSelect={setModule} />
      <AnalyticsBody scope={scope} module={module} />
    </div>
  )
}
